import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDirectionsService } from '../../hooks/useDirectionsService';
import VamosMap from '../../componentsV2/VamosMap';
import { useToasts } from '../../hooks';
import SitesInMarkers from '../../componentsV2/VamosMap/SitesInMarkers';
import DirectionsRendererWrapper from '../../componentsV2/VamosMap/DirectionsRendererWrapper';
import RouteDetailsWrapper from '../../componentsV2/routes/RouteDetailsWrapper';
import Button from '../../componentsV2/Button/Button';
import GetDirectionModal from '../../componentsV2/routes/GetDirectionModal';
import OpenInMapsModal from '../../componentsV2/routes/OpenInMapsModal';
import { handleResponse } from '../../utils/api/errorHandler';
import HeaderWithBreadcrumbs from '../../componentsV2/Headers/HeaderWithBreadcrumbs';
import EditDeleteButtons from '../../componentsV2/EditDeleteButtons/EditDeleteButtons';
import { removeRoute, updateRoute } from '../../utils/api/routes';
import { redirectToRoutesIndex } from '../../utils/navigation';
import RouteAddEditModal from '../../componentsV2/routes/Modal/RouteAddEditModal';
import { DialogContext } from '../../context/DialogContext';
import Icon from '../../componentsV2/Icons/Icon';
import { useUrlModals } from '../../utils/useUrlModals';
import AdvancedSearchWrapper from '../../componentsV2/AdvancedSearch/AdvancedSearchWrapper';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const DEFAULT_SITES = [];
const DEFAULT_ACTIVE_PARAMS = {};
const DEFAULT_SEARCH_PARAMS = {};

const RoutePage = ({
  currentRoute = null,
  sites = DEFAULT_SITES,
  addStop,
  updateRouteState,
  removeStop,
  organizationId: orgID,
  activeParams = DEFAULT_ACTIVE_PARAMS,
  setActiveParams,
  isSearch = false,
  setIsSearch,
  search,
  updateSearchParamsAndUrl,
  searchParams = DEFAULT_SEARCH_PARAMS,
  isSearchExpanded = false,
  setIsSearchExpanded,
}) => {
  const { showToast } = useToasts();
  const { setToLocalStorage } = useLocalStorage();

  const [updatedSites, setUpdatedSites] = useState([]);
  const [isHideMarkers, setIsHideMarkers] = useState(false);
  const [sitesAttributes, setSitesAttributes] = useState({});
  // State to open/close route modal
  const [isSaving, setIsSaving] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isBoundsChanged, setBoundsChanged] = useState(true);
  const [isButtonClicked, setButtonClicked] = useState(false);
  const { openDialog, closeDialog } = useContext(DialogContext);
  const { directions } = useDirectionsService(coordinates);
  const [directionKey, setDirectionKey] = useState('no_zoom');
  const [isZoomedToRoute, setIsZoomedToRoute] = useState(true);
  const routeId = currentRoute?.routeId;
  const name = currentRoute?.name;
  const description = currentRoute?.description;

  // useUrlModals hook
  const { modals, openModal, closeModal } = useUrlModals({
    editRoute: { isIdParam: false },
    getDirections: { isIdParam: false },
    openInMaps: { isIdParam: false },
  });

  // Fetch site attributes
  useEffect(() => {
    fetch(`/api/v3/sites_attributes?organization_id=${orgID}`)
      .then(handleResponse)
      .then(setSitesAttributes)
      .catch(error => showToast({ message: error.message, type: 'error' }));
  }, [showToast, orgID]);

  // Memoized filtered sites
  const stopIndexMap = useMemo(() => {
    const indexMap = {};
    currentRoute?.stops?.forEach(stop => { indexMap[stop.site_id] = true; });
    return indexMap;
  }, [currentRoute?.stops]);

  const filteredSites = useMemo(() => updatedSites.filter(site => stopIndexMap[site.id]), [updatedSites, stopIndexMap]);

  const siteArray = isHideMarkers ? filteredSites : updatedSites;

  // set key for DirectionsRenderer to force re-render when zooming to route next time
  useEffect(() => {
    if (directionKey === 'zoom') {
      setDirectionKey('no_zoom');
    }
  }, [directionKey]);

  useEffect(() => {
    if (isSearchExpanded) return; // only update when advanced search modal is closed
    if (isSearch) {
      setIsEnabled(true); // not displaying search area button when displaying search results
    }
  }, [isSearch, isSearchExpanded]);

  const breadcrumbLinks = [
    { href: '/routes', name: 'Routes' },
    { name },
  ];

  // Route edit/delete handlers
  const handleEdit = () => openModal('editRoute');

  const handleDeleteStop = (stopId) => {
    removeStop(stopId);
    setIsZoomedToRoute(false);
    closeDialog();
  };

  const handleOpenDeleteModal = (stop) => {
    openDialog({
      message: `Do you want to delete the stop ${stop.site_name}?`,
      show: true,
      actions: [
        { label: 'Cancel', variant: 'secondary', onClick: closeDialog },
        { label: 'Delete', variant: 'primary', onClick: () => { handleDeleteStop(stop.id); closeDialog(); } },
      ],
    });
  };

  const handleDeleteRoute = (e) => {
    e.preventDefault();
    removeRoute(routeId, orgID)
      .then(handleResponse)
      .then((data) => {
        // store deletion result in local storage so the Routes index page can show a toast notification
        setToLocalStorage('routeDeletedMessage', data?.success);
        redirectToRoutesIndex();
      })
      .catch(error => { showToast({ message: error.message, type: 'error' }); });
  };

  const handleOpenRouteDeleteModal = (e) => {
    openDialog({
      message: 'Delete the selected Route?',
      show: true,
      actions: [
        { label: 'Cancel', variant: 'secondary', onClick: closeDialog },
        { label: 'Delete route', variant: 'primary', onClick: () => { handleDeleteRoute(e); closeDialog(); } },
      ],
    });
  };

  const itemsForEditDelete = [
    { label: 'Edit', onClick: handleEdit },
    { label: 'Delete', onClick: handleOpenRouteDeleteModal },
  ];

  // Directions modal
  const onOpenDirectionModal = () => {
    if ((currentRoute.stops?.length || 0) < 2) {
      showToast({ message: 'There must be more than one stop to see a path. Please add another stop', type: 'error' });
      openModal('getDirections'); // still open to show empty modal
    } else {
      // eslint-disable-next-line no-unused-expressions
      modals.getDirections?.isOpen ? closeModal('getDirections') : openModal('getDirections');
    }
  };

  const hideOffRouteSites = () => {
    if (isHideMarkers) {
      setIsHideMarkers(false);
    } else {
      setIsHideMarkers(true);
    }
  };

  const zoomToRoute = () => {
    setDirectionKey('zoom');
    setIsSearch(false);
    setIsZoomedToRoute(true);
  };

  const searchTitle = 'Add sites to route';

  // Update coordinates when stops change
  useEffect(() => {
    setCoordinates(currentRoute?.stops?.map(stop => ({ lat: stop.location.latitude, lng: stop.location.longitude })) || []);
  }, [currentRoute?.stops]);

  // Map sites with coords
  useEffect(() => {
    if (sites?.length) {
      const sitesWithCoords = sites.map(site => ({ coords: { lat: site.latitude, lng: site.longitude }, ...site }));
      setUpdatedSites(sitesWithCoords);
    }
  }, [sites]);

  // Save route edit
  const handleEditRoute = (data) => {
    setIsSaving(true);
    updateRoute(data, orgID, routeId)
      .then(handleResponse)
      .then(() => { updateRouteState({ ...currentRoute, ...data }); closeModal('editRoute'); showToast({ message: 'Route updated!', type: 'success' }); })
      .catch(error => showToast({ message: error.message, type: 'error' }))
      .finally(() => setIsSaving(false));
  };

  const displayMapAndDirectionButtons = () => {
    const invalidStopIndex = currentRoute.stops.findIndex(stop => Number.isNaN(Number(stop.distance)));
    const invalidStop = invalidStopIndex !== -1 ? currentRoute.stops[invalidStopIndex] : null;
    const invalidStopPrev = currentRoute.stops[invalidStopIndex - 1];

    if (invalidStop) {
      return <div className="text-vamos-red mt-5">Sorry, we could not calculate driving directions from <strong>{`${invalidStopPrev.site_name}`}</strong> to <strong>{`${invalidStop.site_name}`}</strong>.</div>;
    }

    if (coordinates.length < 2) {
      return <div className="mt-5">Add at least 2 route stops to see driving directions.</div>;
    }

    return (
      <div className="flex gap-4 justify-end">
        <button type="button" onClick={() => openModal('openInMaps')}><OpenInMapsModal /></button>
        <Button label="Get directions" size="md" className="text-lg font-medium float-right mt-5" onClick={onOpenDirectionModal} />
      </div>
    );
  };

  if (!currentRoute) {
    return (
      <div className="ml-2.75">
        <p>Loading route...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="ml-2.75">
        <header>
          {/* Breadcrumbs Section */}
          <div className="flex justify-between items-start w-full">
            <HeaderWithBreadcrumbs links={breadcrumbLinks} name="route" label="Routes" size="h-9" shouldNotDisplayHeader />
          </div>

          {/* Icon, Title and Buttons ABOVE the horizontal line */}
          <div className="flex justify-between items-center w-full border-b border-vamos-blue-gray pb-3 mr-50.5 mb-6">
            <div className="flex items-center gap-2.25">
              <Icon name="route" className="h-6" />
              <h2 className="font-bold text-2xl">{name}</h2>
            </div>
            {/* Right: Edit and Delete Buttons */}
            <EditDeleteButtons items={itemsForEditDelete} />
          </div>
        </header>

        {/* Description and Search BELOW the line */}
        <div className="flex gap-10 pb-3">
          {/* Description */}
          <p className="flex-1 min-w-107">
            {description}
          </p>
          {/* Search Section */}
          <div className="flex-2">
            <AdvancedSearchWrapper
              setIsSearchExpanded={setIsSearchExpanded}
              sitesAttributes={sitesAttributes}
              params={activeParams}
              setParams={setActiveParams}
              search={search}
              searchParams={searchParams}
              setSearchParams={setActiveParams}
              updateSearchParamsAndUrl={updateSearchParamsAndUrl}
              isSearchExpanded={isSearchExpanded}
              isRoutePage
              searchTitle={searchTitle}
              hideOffRouteSites={hideOffRouteSites}
              zoomToRoute={zoomToRoute}
              isHideMarkers={isHideMarkers}
            />
          </div>
        </div>
        <h2 className="text-vamos-black font-medium text-lg">
          Route Details
        </h2>

        <div className="flex gap-10 mt-3">
          <div className="flex-1">
            <RouteDetailsWrapper currentRoute={currentRoute} handleOpenDeleteModal={handleOpenDeleteModal} />
            {displayMapAndDirectionButtons()}
            {modals.getDirections?.isOpen && (
              <GetDirectionModal
                closeModal={() => closeModal('getDirections')}
                directions={directions}
                isOpenDirectionModal={modals.getDirections?.isOpen}
                currentRouteStops={currentRoute.stops}
                routeName={currentRoute.name}
              />
            )}
          </div>

          <div className="flex-2 z-0 h-fit">
            <VamosMap
              height="80vh"
              className="h-fit rounded-md overflow-hidden"
              isEnabled={isEnabled}
              setIsEnabled={setIsEnabled}
              isBoundsChanged={isBoundsChanged}
              isButtonClicked={isButtonClicked}
              setButtonClicked={setButtonClicked}
            >
              {directions && coordinates.length > 1 && ( // only render when we have more than 1 route coordinate
                (
                <DirectionsRendererWrapper
                  key={directionKey} // rerender if zoom
                  directions={directions}
                  shouldPreserveViewport={isSearch || !isZoomedToRoute}
                />
                )
              )}
              <SitesInMarkers
                key={isHideMarkers ? 'route-stops-only' : 'all-sites'}
                sitesArray={siteArray}
                addStop={addStop}
                setBoundsChanged={setBoundsChanged}
                isButtonClicked={isButtonClicked}
                setButtonClicked={setButtonClicked}
                currentRoute={currentRoute}
                isSearch={isSearch}
                setIsZoomedToRoute={setIsZoomedToRoute}
              />
            </VamosMap>
          </div>
        </div>
      </div>
      {/* Edit Route Modal */}
      {modals.editRoute?.isOpen && (
        <RouteAddEditModal
          onCloseModal={() => closeModal('editRoute')}
          title="Edit Route"
          data={currentRoute}
          onSubmit={handleEditRoute}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};

RoutePage.propTypes = {
  currentRoute: PropTypes.object,
  sites: PropTypes.array,
  addStop: PropTypes.func,
  updateRouteState: PropTypes.func,
  removeStop: PropTypes.func,
  organizationId: PropTypes.number,
  activeParams: PropTypes.object,
  setActiveParams: PropTypes.func,
  isSearch: PropTypes.bool,
  setIsSearch: PropTypes.func,
  search: PropTypes.func,
  updateSearchParamsAndUrl: PropTypes.func,
  searchParams: PropTypes.object,
  isSearchExpanded: PropTypes.bool,
  setIsSearchExpanded: PropTypes.func,
};

export default RoutePage;
